import { useState, useEffect, useRef } from 'react'
import { createClient } from '@supabase/supabase-js'
import axios from 'axios'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

const API = process.env.REACT_APP_API_URL

function normalizarTexto(texto) {
  return (texto || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

// La ruta de subir audio ahora exige esta cabecera en el backend (antes
// cualquiera podia llamarla sin iniciar sesion — ver
// api/src/middleware/verificarAdmin.js).
function cabeceras(usuario) {
  return { headers: { 'x-usuario-id': usuario?.id } }
}

export default function SalaGrabacion({ usuario }) {
  const [niveles,       setNiveles]       = useState([])
  const [nivelActivo,   setNivelActivo]   = useState(null)
  const [unidades,      setUnidades]      = useState([])
  const [unidadActiva,  setUnidadActiva]  = useState(null)
  const [lecciones,     setLecciones]     = useState([])
  const [leccionActiva, setLeccionActiva] = useState(null)
  const [textos,        setTextos]        = useState([])
  const [mapaAudios,    setMapaAudios]    = useState({})
  const [indice,        setIndice]        = useState(0)
  const [grabando,      setGrabando]      = useState(false)
  const [audioTemporal, setAudioTemporal] = useState(null)
  const [subiendo,      setSubiendo]      = useState(false)
  const [cargando,      setCargando]      = useState(true)

  const mediaRecorderRef = useRef(null)
  const chunksRef        = useRef([])
  const inputArchivoRef  = useRef(null)

  useEffect(() => {
    axios.get(API + '/curriculo/niveles').then(r => {
      setNiveles(r.data.datos || [])
      if (r.data.datos?.length > 0) setNivelActivo(r.data.datos[0])
    })
    cargarMapaAudios()
  }, [])

  useEffect(() => {
    if (!nivelActivo) return
    setUnidades([]); setUnidadActiva(null); setLecciones([]); setLeccionActiva(null)
    axios.get(API + '/curriculo/niveles/' + nivelActivo.id + '/unidades')
      .then(r => setUnidades(r.data.datos || []))
  }, [nivelActivo])

  useEffect(() => {
    if (!unidadActiva) return
    setLecciones([]); setLeccionActiva(null)
    axios.get(API + '/curriculo/unidades/' + unidadActiva.id + '/lecciones')
      .then(r => setLecciones(r.data.datos || []))
  }, [unidadActiva])

  useEffect(() => {
    if (!leccionActiva) return
    setCargando(true)
    setIndice(0)
    axios.get(API + '/audios/leccion/' + leccionActiva.id + '/textos')
      .then(r => setTextos(r.data.datos || []))
      .finally(() => setCargando(false))
  }, [leccionActiva])

  function cargarMapaAudios() {
    axios.get(API + '/audios/mapa').then(r => setMapaAudios(r.data.datos || {}))
  }

  const itemActual = textos[indice]
  const yaGrabado = itemActual && mapaAudios[normalizarTexto(itemActual.texto)]
  const grabadosEnLeccion = textos.filter(t => mapaAudios[normalizarTexto(t.texto)]).length

  async function iniciarGrabacion() {
    setAudioTemporal(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      chunksRef.current = []
      mr.ondataavailable = e => chunksRef.current.push(e.data)
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        setAudioTemporal(blob)
        stream.getTracks().forEach(t => t.stop())
      }
      mr.start()
      mediaRecorderRef.current = mr
      setGrabando(true)
    } catch (err) {
      alert('No se pudo acceder al microfono. Revisa los permisos del navegador.')
    }
  }

  function detenerGrabacion() {
    mediaRecorderRef.current?.stop()
    setGrabando(false)
  }

  // Permite elegir un archivo de audio ya grabado (por ejemplo, grabado en el
  // celular y guardado en el PC) en vez de grabar en vivo con el microfono.
  // Acepta mp3, wav, m4a, ogg, webm, etc. — lo que el navegador pueda reproducir.
  function archivoSeleccionado(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setAudioTemporal(archivo)
    e.target.value = '' // permite volver a elegir el mismo archivo despues si hace falta
  }

  // Adivina la extension y el tipo MIME correctos tanto para una grabacion en
  // vivo (siempre .webm) como para un archivo subido (usa su propio tipo).
  function datosArchivoAudio(audio) {
    if (audio instanceof File && audio.type) {
      const ext = (audio.name?.split('.').pop() || audio.type.split('/')[1] || 'webm').toLowerCase()
      return { extension: ext, tipo: audio.type }
    }
    return { extension: 'webm', tipo: 'audio/webm' }
  }

  async function guardarGrabacion() {
    if (!audioTemporal || !itemActual) return
    setSubiendo(true)
    try {
      const { extension, tipo } = datosArchivoAudio(audioTemporal)
      const nombreArchivo = 'audio_' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.' + extension
      const { error: errorSubida } = await supabase.storage
        .from('audios-grabados')
        .upload(nombreArchivo, audioTemporal, { contentType: tipo })
      if (errorSubida) throw errorSubida

      const { data: urlData } = supabase.storage.from('audios-grabados').getPublicUrl(nombreArchivo)

      await axios.post(API + '/audios/guardar', {
        texto: itemActual.texto,
        audio_url: urlData.publicUrl,
        leccion_id: leccionActiva.id
      }, cabeceras(usuario))

      setMapaAudios(m => ({ ...m, [normalizarTexto(itemActual.texto)]: urlData.publicUrl }))
      setAudioTemporal(null)
      if (indice < textos.length - 1) setIndice(i => i + 1)
    } catch (err) {
      console.error(err)
      alert('Error al guardar el audio. Intenta de nuevo.')
    }
    setSubiendo(false)
  }

  function descartarGrabacion() {
    setAudioTemporal(null)
  }

  const inputStyle = {
    width:'100%', padding:'9px 12px', border:'1.5px solid #E2E6F0',
    borderRadius:8, fontFamily:'inherit', fontSize:13, cursor:'pointer'
  }

  return (
    <div style={{ maxWidth:1000, margin:'0 auto', padding:'0 4px' }}>

      <div style={{ background:'linear-gradient(135deg,#7C3AED,#4F46E5)', borderRadius:16, padding:24, marginBottom:24, color:'#fff' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:32 }}>🎙</span>
          <div>
            <h1 style={{ fontSize:20, fontWeight:800, marginBottom:2 }}>Sala de grabacion</h1>
            <p style={{ fontSize:13, opacity:.85 }}>Graba con tu voz cada palabra y frase para reemplazar la voz robotica.</p>
          </div>
        </div>
      </div>

      {/* Selectores nivel / unidad / leccion */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:20 }}>
        <select style={inputStyle} value={nivelActivo?.id || ''} onChange={e => setNivelActivo(niveles.find(n => n.id === e.target.value))}>
          {niveles.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
        </select>
        <select style={inputStyle} value={unidadActiva?.id || ''} onChange={e => setUnidadActiva(unidades.find(u => u.id === e.target.value))}>
          <option value=''>Selecciona unidad</option>
          {unidades.map(u => <option key={u.id} value={u.id}>{u.titulo}</option>)}
        </select>
        <select style={inputStyle} value={leccionActiva?.id || ''} onChange={e => setLeccionActiva(lecciones.find(l => l.id === e.target.value))}>
          <option value=''>Selecciona leccion</option>
          {lecciones.map(l => <option key={l.id} value={l.id}>{l.titulo}</option>)}
        </select>
      </div>

      {!leccionActiva && (
        <div style={{ textAlign:'center', padding:60, color:'#6B7280', background:'#fff', borderRadius:14, border:'1px solid #E2E6F0' }}>
          Selecciona un nivel, unidad y leccion arriba para empezar a grabar.
        </div>
      )}

      {leccionActiva && cargando && (
        <div style={{ textAlign:'center', padding:60, color:'#6B7280' }}>Cargando textos de la leccion...</div>
      )}

      {leccionActiva && !cargando && textos.length === 0 && (
        <div style={{ textAlign:'center', padding:60, color:'#6B7280', background:'#fff', borderRadius:14, border:'1px solid #E2E6F0' }}>
          Esta leccion no tiene textos con audio para grabar.
        </div>
      )}

      {leccionActiva && !cargando && textos.length > 0 && (
        <div>
          {/* Progreso de la leccion */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <div style={{ flex:1, height:8, background:'#E2E6F0', borderRadius:999, overflow:'hidden' }}>
              <div style={{ width: (grabadosEnLeccion/textos.length*100) + '%', height:'100%', background:'#22C55E', borderRadius:999, transition:'width .3s ease' }} />
            </div>
            <span style={{ fontSize:13, fontWeight:700, color:'#374151', whiteSpace:'nowrap' }}>
              {grabadosEnLeccion} / {textos.length} grabados
            </span>
          </div>

          {/* Lista de textos (miniatura, clickeable) */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:24 }}>
            {textos.map((t, i) => {
              const grabado = mapaAudios[normalizarTexto(t.texto)]
              return (
                <button key={i} onClick={() => { setIndice(i); setAudioTemporal(null) }}
                  style={{ width:32, height:32, borderRadius:8, border: i===indice ? '2px solid #7C3AED' : '1px solid #E2E6F0',
                    background: grabado ? '#F0FDF4' : '#fff', color: grabado ? '#22C55E' : '#9CA3AF',
                    fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  {grabado ? '✓' : i+1}
                </button>
              )
            })}
          </div>

          {/* Tarjeta de grabacion */}
          {itemActual && (
            <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:16, padding:32, textAlign:'center' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#7C3AED', textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>
                {itemActual.contexto} · {indice+1} de {textos.length}
              </div>
              <div style={{ fontSize:24, fontWeight:800, color:'#1A1A2E', marginBottom:24, lineHeight:1.4 }}>
                {itemActual.texto}
              </div>

              {yaGrabado && !audioTemporal && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:13, color:'#22C55E', fontWeight:700, marginBottom:10 }}>✅ Ya tiene audio grabado</div>
                  <audio controls src={yaGrabado} style={{ width:'100%', maxWidth:360 }} />
                </div>
              )}

              {audioTemporal && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:13, color:'#7C3AED', fontWeight:700, marginBottom:10 }}>🎧 Escucha tu grabacion</div>
                  <audio controls src={URL.createObjectURL(audioTemporal)} style={{ width:'100%', maxWidth:360 }} />
                </div>
              )}

              <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
                {!grabando && !audioTemporal && (
                  <button onClick={iniciarGrabacion}
                    style={{ background:'#EF4444', color:'#fff', border:'none', borderRadius:12, padding:'14px 28px', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
                    🎙 {yaGrabado ? 'Regrabar' : 'Grabar'}
                  </button>
                )}
                {!grabando && !audioTemporal && (
                  <>
                    <input ref={inputArchivoRef} type='file' accept='audio/*' onChange={archivoSeleccionado} style={{ display:'none' }} />
                    <button onClick={() => inputArchivoRef.current?.click()}
                      style={{ background:'#fff', color:'#7C3AED', border:'1.5px solid #7C3AED', borderRadius:12, padding:'14px 28px', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
                      📁 Subir archivo
                    </button>
                  </>
                )}
                {grabando && (
                  <button onClick={detenerGrabacion}
                    style={{ background:'#374151', color:'#fff', border:'none', borderRadius:12, padding:'14px 28px', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer', animation:'pulse 1s infinite' }}>
                    ⏹ Detener ({'grabando...'})
                  </button>
                )}
                {audioTemporal && (
                  <>
                    <button onClick={descartarGrabacion}
                      style={{ background:'#F4F6FA', color:'#6B7280', border:'none', borderRadius:12, padding:'14px 24px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                      Descartar
                    </button>
                    <button onClick={guardarGrabacion} disabled={subiendo}
                      style={{ background:'#22C55E', color:'#fff', border:'none', borderRadius:12, padding:'14px 28px', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
                      {subiendo ? 'Guardando...' : '✅ Guardar y siguiente'}
                    </button>
                  </>
                )}
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:28, paddingTop:20, borderTop:'1px solid #F4F6FA' }}>
                <button onClick={() => { setIndice(i => Math.max(0, i-1)); setAudioTemporal(null) }} disabled={indice === 0}
                  style={{ background:'none', border:'none', color: indice===0 ? '#D1D5DB' : '#6B7280', fontSize:13, cursor: indice===0 ? 'default' : 'pointer', fontFamily:'inherit' }}>
                  ← Anterior
                </button>
                <button onClick={() => { setIndice(i => Math.min(textos.length-1, i+1)); setAudioTemporal(null) }} disabled={indice === textos.length-1}
                  style={{ background:'none', border:'none', color: indice===textos.length-1 ? '#D1D5DB' : '#6B7280', fontSize:13, cursor: indice===textos.length-1 ? 'default' : 'pointer', fontFamily:'inherit' }}>
                  Saltar sin grabar →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
